import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';

export type SystemCategory = {
  id: number;
  category_type: string;
  value: string;
  label: string;
  sort_order: number;
  is_active: boolean;
  is_system: boolean;
  // F3: descriptive fields shown to the collaborator (objetivo / qué incluye).
  objective: string | null;
  includes: string | null;
};

/** Active configurable categories for a given type (e.g. 'course_category'). */
export function useCategories(categoryType: string) {
  return useQuery({
    queryKey: ['categories', 'type', categoryType],
    queryFn: () => api.get<SystemCategory[]>('/categories', { category_type: categoryType }),
    select: (res) => res.data,
    staleTime: 10 * 60 * 1000,
  });
}

/**
 * Returns a resolver mapping a category value to its human label, sourced from
 * the configurable system_categories. Falls back to the raw value only if it
 * isn't found (shouldn't happen for real values). Use this everywhere a category
 * is shown so admin-created categories never render as raw slugs.
 */
export function useCategoryLabel(categoryType: string) {
  const { data } = useCategories(categoryType);
  return useMemo(() => {
    const map = new Map((data ?? []).map((c) => [c.value, c.label]));
    return (value: string | null | undefined): string => (value ? (map.get(value) ?? value) : '—');
  }, [data]);
}

/**
 * Resolver from a category value to its full configurable record, so the UI can
 * show descriptive fields (objetivo / qué incluye) alongside the label.
 */
export function useCategoryInfo(categoryType: string) {
  const { data } = useCategories(categoryType);
  return useMemo(() => {
    const map = new Map((data ?? []).map((c) => [c.value, c]));
    return (value: string | null | undefined): SystemCategory | undefined =>
      value ? map.get(value) : undefined;
  }, [data]);
}
